import React, { useEffect, useState } from "react";
import { Share, SquarePlus, Smartphone, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { trackPageView } from "../utils/track";

const KEY = "lp-install-dismissed";
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

export const InstallPrompt = () => {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [native, setNative] = useState(null);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    try { if (localStorage.getItem(KEY)) return; } catch { return; }
    const onPrompt = (e) => { e.preventDefault(); setNative(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS has no install event — show the Add to Home Screen hint after the shopper has had a look around
    const t = isIos() ? setTimeout(() => setShow(true), 8000) : null;
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setShow(false); };
  const install = async () => {
    if (!native) return;
    native.prompt();
    const { outcome } = await native.userChoice;
    if (outcome === "accepted") trackPageView("/pwa-install");
    dismiss();
  };

  if (!show) return null;
  const ios = isIos() && !native;

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] inset-x-4 z-[55] lp-fade-up sm:left-auto sm:right-6 sm:max-w-sm" data-testid="install-prompt">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <img src="/icons/icon-192.png" alt="" className="h-12 w-12 rounded-xl shrink-0 border border-border" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-foreground">{t("ip.addLytepressToYour")}</p>
            {ios ? (
              <p className="text-sm text-muted-foreground mt-1">
                Tap <Share size={14} className="inline -mt-0.5 text-[#2563eb]" /> <span className="font-semibold text-foreground">Share</span>, then <SquarePlus size={14} className="inline -mt-0.5" /> <span className="font-semibold text-foreground">{t("ip.addToHomeScreen")}</span> — your codes and deals one tap away.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">{t("ip.fasterAccessToDeals")}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              {!ios && <button onClick={install} data-testid="install-prompt-install" className="inline-flex items-center gap-1.5 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"><Smartphone size={14} /> Install</button>}
              <button onClick={dismiss} data-testid="install-prompt-dismiss" className="text-sm font-semibold text-muted-foreground hover:text-foreground px-3 py-2">{ios ? "Got it" : "Not now"}</button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground shrink-0"><X size={18} /></button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
